/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { FormHandles } from '@unform/core';

import getValidationErros from '~/utils/getValidationsErrors';
import { formatPrice } from '~/utils/format';
import api from '~/services/api';

import { Container } from './styles';
import { KrInfo } from '~/components/KoroIcons';
import InputCheckbox, {
  IOption as IOptionCheckbox,
} from '~/components/InputCheckbox';
import Select, { IOption } from '~/components/Select';
import Input from '~/components/Input';
import InputMask from '~/components/InputMask';
import { IBatch } from '../Batches';
import { IInitialData as IBasicData } from '../BasicData';
import Loading from '~/components/Loading';
import { IModalityData } from '../Modalities';

export interface IPaymentMethod {
  id: number;
  name: string;
}

interface IContract {
  id: number;
  code: string;
  fantasy_name: string;
  tax_type: string;
  tax: string;
  service_type: string;
  service: string;
  contractsValues: any[];
}

export interface IEventPaymentMethod {
  id: number;
  payment_method_id: number;
  installments?: number;
}

export interface ITarget {
  registration: number;
  revenue: number;
  avarageTicket: number;
}

interface IFinancial {
  batches: IBatch[];
  modalities: IModalityData[];
  basicData: IBasicData;
  sold?: boolean;
  statusName?: string;
  targets?: {
    registration: number;
    revenue: number;
    avarageTicket: number;
  };
  eventPaymentsMethods?: IEventPaymentMethod[];
  onChange(
    paymentsMethods: IEventPaymentMethod[],
    basicData: IBasicData,
    batches: IBatch[],
    targets: ITarget
  ): void;
  progressNumber?: number;
}

export interface IFinancialHandle {
  submit: () => void;
}

const Financial: ForwardRefRenderFunction<IFinancialHandle, IFinancial> = (
  {
    basicData,
    batches,
    sold,
    targets: eventTargets,
    eventPaymentsMethods,
    onChange,
    statusName,
    progressNumber,
  },
  ref
) => {
  const formRef = useRef<FormHandles>(null);

  const [totalPrices, setTotalPrices] = useState(0);
  const [totalQtdPlaces, setTotalQtdPlaces] = useState(0);
  const [maxInstallments, setMaxInstallments] = useState(0);
  const [beforePayment, setBeforePayment] = useState(0);
  const [uniqueTicket, setUniqueTicket] = useState(false);
  const [targets, setTargets] = useState({
    registration: 0,
    revenue: 0,
    avarageTicket: 0,
  });
  const [paymentsMethods, setPaymentsMethods] = useState<IPaymentMethod[]>([]);
  const [freePayment, setFreePayment] = useState(false);
  const [pixPayment, setPixPayment] = useState(false);
  const [billetPayment, setBilletPayment] = useState(false);
  const [debitPayment, setDebitPayment] = useState(false);
  const [creditPayment, setCreditPayment] = useState(false);
  const [contractsOptions, setContractsOptions] = useState<IOption[]>([]);
  const [contractSelected, setContractSelected] = useState({} as IContract);
  const [contracts, setContracts] = useState<IContract[]>([]);
  const [oldPaymentsMethods, setOldPaymentsMethods] = useState<
    IEventPaymentMethod[]
  >([]);
  const [selectedPaymentsMethods, setSelectedPaymentsMethods] = useState<
    IEventPaymentMethod[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Carregando...');
  const [disabledFreePayment, setDisabledFreePayment] = useState(true);
  const [disabledPixPayment, setDisabledPixPayment] = useState(true);
  const [disabledBilletPayment, setDisabledBilletPayment] = useState(true);
  const [disabledDebitPayment, setDisabledDebitPayment] = useState(true);
  const [disabledCreditPayment, setDisabledCreditPayment] = useState(true);
  const [paymentMethodsOptions, setPaymentMethodsOptions] = useState<
    IOptionCheckbox[]
  >([]);

  useEffect(() => {
    if (progressNumber && progressNumber === 9) {
      api
        .delete(`batches-categories/events/${basicData.event_id}`)
        .catch((err) => {
          console.log(err);
        });
    }
  }, [basicData?.event_id, progressNumber]);

  useEffect(() => {
    const allPrices = batches.map((acc) => {
      return acc.batchesCategories?.reduce((total, batchCategory) => {
        const newPrices =
          total +
          (batchCategory.quantity === 0
            ? Infinity
            : parseFloat(batchCategory.price) * batchCategory.quantity);

        return newPrices;
      }, 0);
    });

    const total = allPrices.reduce((a, b) => (a ?? 0) + (b ?? 0), 0);

    setTotalPrices(total as number);

    const allQtdPlaces = batches.reduce((prev, acc) => {
      const totalQtd = acc.batchesCategories?.reduce(
        (totalPlaces, batchCategory) => {
          const quantity =
            parseInt(batchCategory.quantity.toString(), 10) || Infinity;
          return totalPlaces + quantity;
        },
        0
      ) as number;

      const quantity =
        parseInt(acc.qtdPlaces?.toString() || '0', 10) || Infinity;
      if (totalQtd < quantity) {
        return prev + totalQtd;
      }
      return prev + quantity;
    }, 0);

    setTotalQtdPlaces(allQtdPlaces);
  }, [batches]);

  useEffect(() => {
    if (
      !eventTargets ||
      (!eventTargets.registration && !eventTargets.revenue)
    ) {
      setTargets({
        registration: totalQtdPlaces,
        revenue: totalPrices,
        avarageTicket:
          totalQtdPlaces && totalPrices !== Infinity
            ? totalPrices / totalQtdPlaces
            : 0,
      });
    }
  }, [totalQtdPlaces, totalPrices, eventTargets]);

  useEffect(() => {
    if (eventTargets) {
      setTargets(eventTargets);
    }

    if (eventPaymentsMethods && eventPaymentsMethods.length > 0) {
      setSelectedPaymentsMethods(eventPaymentsMethods);
      setOldPaymentsMethods(eventPaymentsMethods);
      const freePaymentData = paymentsMethods.find(
        (paymentMethod) => paymentMethod.name === 'Gratuito'
      );
      const pixPaymentData = paymentsMethods.find(
        (paymentMethod) => paymentMethod.name === 'Pix'
      );
      const creditPaymentData = paymentsMethods.find(
        (paymentMethod) => paymentMethod.name === 'Crédito'
      );

      if (freePaymentData) {
        const hasFree = eventPaymentsMethods.some(
          (paymentMethod) =>
            paymentMethod.payment_method_id === freePaymentData.id
        );

        setFreePayment(hasFree);
      }

      if (pixPaymentData) {
        const hasPix = eventPaymentsMethods.some(
          (paymentMethod) =>
            paymentMethod.payment_method_id === pixPaymentData.id
        );

        setPixPayment(hasPix);
      }

      if (creditPaymentData) {
        const hasCredit = eventPaymentsMethods.find(
          (paymentMethod) =>
            paymentMethod.payment_method_id === creditPaymentData.id
        );
        setMaxInstallments(hasCredit?.installments || 1);
        setCreditPayment(!!hasCredit);
      }
    }
  }, [eventPaymentsMethods, eventTargets, paymentsMethods]);

  useEffect(() => {
    const avarageTicket = targets.revenue / targets.registration;

    setTargets((oldStats) => ({
      ...oldStats,
      avarageTicket: Number.isNaN(avarageTicket) ? 0 : avarageTicket,
    }));
  }, [targets.registration, targets.revenue]);

  useEffect(() => {
    api
      .get<IContract[]>('contracts/organizers', {
        params: { status: 'Ativo' },
      })
      .then((response) => {
        const data = response.data.map((contract) => ({
          id: contract.id,
          value: `#${contract.code} - ${contract.fantasy_name}`,
          selected: basicData.contract_id === contract.id,
        }));

        const selectedConstract = data.find((contract) => contract.selected);

        if (!selectedConstract) {
          data[0].selected = true;
        } else {
          const findIndex = data.findIndex(
            (contract) => contract.id === selectedConstract.id
          );
          data[findIndex].selected = true;
        }

        const selectedContractData = response.data.find(
          (contract) => contract.id === selectedConstract?.id
        );

        setContracts(response.data);
        if (selectedContractData) {
          setContractSelected(selectedContractData);
        } else if (response.data[0]) {
          setContractSelected(response.data[0]);
        }
        setContractsOptions(data);
      });
  }, [basicData, basicData.contract_id]);

  useEffect(() => {
    api.get<IPaymentMethod[]>('payments-methods').then((response) => {
      setPaymentsMethods(response.data);
    });
  }, []);

  useEffect(() => {
    setBeforePayment(Number(basicData.fill_before));
  }, [basicData.fill_before]);

  useEffect(() => {
    setUniqueTicket(Boolean(basicData.unique_ticket));
  }, [basicData.unique_ticket]);

  useEffect(() => {
    if (Object.keys(contractSelected).length > 0) {
      setDisabledFreePayment(
        !contractSelected.contractsValues.some(
          (contractValue: any) => contractValue.type === 'Gratuito'
        )
      );
      setDisabledPixPayment(
        !contractSelected.contractsValues.some(
          (contractValue: any) => contractValue.type === 'Pix'
        )
      );
      setDisabledBilletPayment(
        contractSelected.contractsValues.some(
          (contractValue: any) => contractValue.type === 'Boleto'
        )
      );
      setDisabledDebitPayment(
        !contractSelected.contractsValues.some(
          (contractValue: any) => contractValue.type === 'Débito'
        )
      );
      setDisabledCreditPayment(
        !contractSelected.contractsValues.some(
          (contractValue: any) => contractValue.type === 'Crédito'
        )
      );
    }
  }, [contractSelected]);

  useEffect(() => {
    setPaymentMethodsOptions(
      paymentsMethods.map<IOptionCheckbox>((paymentMethod) => {
        let selected = true;
        let disabled = false;
        if (paymentMethod.name === 'Gratuito') {
          selected = freePayment;
          disabled = disabledFreePayment;
        }
        if (paymentMethod.name === 'Pix') {
          selected = freePayment ? false : pixPayment;
          disabled = disabledPixPayment;
        }
        if (paymentMethod.name === 'Boleto') {
          selected = false;
          disabled = disabledBilletPayment || true;
        }
        if (paymentMethod.name === 'Débito') {
          selected = false;
          disabled = disabledDebitPayment || true;
        }
        if (paymentMethod.name === 'Crédito') {
          selected = freePayment ? false : creditPayment;
          disabled = disabledCreditPayment;
        }
        return {
          label: paymentMethod.name,
          value: paymentMethod.id,
          selected,
          disabled,
        };
      })
    );
  }, [
    creditPayment,
    disabledBilletPayment,
    disabledCreditPayment,
    disabledDebitPayment,
    disabledFreePayment,
    disabledPixPayment,
    freePayment,
    paymentsMethods,
    pixPayment,
  ]);

  const installments = useMemo(
    () => [
      { id: 1, value: '1', selected: maxInstallments === 1 },
      { id: 2, value: '2', selected: maxInstallments === 2 },
      { id: 3, value: '3', selected: maxInstallments === 3 },
      { id: 4, value: '4', selected: maxInstallments === 4 },
      { id: 5, value: '5', selected: maxInstallments === 5 },
      { id: 6, value: '6', selected: maxInstallments === 6 },
      { id: 7, value: '7', selected: maxInstallments === 7 },
      { id: 8, value: '8', selected: maxInstallments === 8 },
      { id: 9, value: '9', selected: maxInstallments === 9 },
      { id: 10, value: '10', selected: maxInstallments === 10 },
      { id: 11, value: '11', selected: maxInstallments === 11 },
      { id: 12, value: '12', selected: maxInstallments === 12 },
    ],
    [maxInstallments]
  );

  const handleChangeMaxInstallments = useCallback((option) => {
    setMaxInstallments(parseInt(option.value, 10));
  }, []);

  const handleTargetRegistration = useCallback(
    (e) => {
      setTargets({
        ...targets,
        registration: parseInt(e.target.value, 10),
      });
    },
    [targets]
  );

  const handleTargetRevenue = useCallback(
    (e) => {
      setTargets({
        ...targets,
        revenue: parseFloat(
          e.target.value.replace('R$', '').replaceAll('.', '').replace(',', '.')
        ),
      });
    },
    [targets]
  );

  const handleChangeContract = useCallback(
    (contractOption) => {
      setFreePayment(false);
      setPixPayment(false);
      setBilletPayment(false);
      setDebitPayment(false);
      setCreditPayment(false);
      setMaxInstallments(0);

      setPaymentMethodsOptions(
        paymentsMethods.map<IOptionCheckbox>((paymentMethod) => ({
          label: paymentMethod.name,
          value: paymentMethod.id,
          selected: false,
          disabled: false,
        }))
      );

      const contractSelectedData = contracts.find(
        (contract) => contract.id === contractOption.id
      );
      if (contractSelectedData) {
        setContractSelected(contractSelectedData);
      }
    },
    [contracts, paymentsMethods]
  );

  const handleChangePaymentsMethods = useCallback(
    (paymentsMethodsData: IOptionCheckbox[]) => {
      const hasFree = paymentsMethodsData.some(
        (paymentMethod) =>
          paymentMethod.label === 'Gratuito' && paymentMethod.selected
      );
      const hasPix = paymentsMethodsData.some(
        (paymentMethod) =>
          paymentMethod.label === 'Pix' && paymentMethod.selected
      );
      const hasBillet = paymentsMethodsData.some(
        (paymentMethod) =>
          paymentMethod.label === 'Boleto' && paymentMethod.selected
      );
      const hasDebit = paymentsMethodsData.some(
        (paymentMethod) =>
          paymentMethod.label === 'Débito' && paymentMethod.selected
      );
      const hasCredit = paymentsMethodsData.some(
        (paymentMethod) =>
          paymentMethod.label === 'Crédito' && paymentMethod.selected
      );

      setFreePayment(hasFree);
      setPixPayment(hasPix);
      setBilletPayment(hasBillet);
      setDebitPayment(hasDebit);
      setCreditPayment(hasCredit);
    },
    []
  );

  useEffect(() => {
    if (freePayment) {
      setPixPayment(false);
      setBilletPayment(false);
      setDebitPayment(false);
      setCreditPayment(false);
    }
  }, [freePayment, pixPayment, billetPayment, debitPayment, creditPayment]);

  const handleSubmit = useCallback(
    async (data) => {
      if (loading) return;
      try {
        setLoading(true);
        setLoadingText('Salvando...');
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          contract: Yup.string().required('O contrato é obrigatório'),
          serviceTax: Yup.string().required('O tipo é obrigatório'),
          processingTax: Yup.string().required('O status é obrigatório'),
          targetRegistration: Yup.string().required(
            'A meta de inscrições é obrigatória'
          ),
          targetRevenue: Yup.string().required(
            'A meta de receita é obrigatória'
          ),
          targetAvarageTicket: Yup.string().required(
            'O ticket médio é obrigatório'
          ),
          paymentsMethods: Yup.string().when(`$checkPaymentsMethods`, {
            is: (checkPaymentsMethods: boolean) => checkPaymentsMethods,
            then: Yup.string().required(
              'Escolha pelo menos uma forma de pagamento'
            ),
            otherwise: Yup.string(),
          }),
          maxInstallments: Yup.string().when(`$checkMaxInstallments`, {
            is: (checkMaxInstallments: boolean) => checkMaxInstallments,
            then: Yup.string().required(
              'A quantidade de parcelas é obrigatória'
            ),
            otherwise: Yup.string(),
          }),
        });

        await schema.validate(data, {
          abortEarly: false,
          context: {
            checkPaymentsMethods: !paymentMethodsOptions.some(
              (paymentMethod) => paymentMethod.selected
            ),
            checkMaxInstallments: paymentMethodsOptions.some(
              (paymentMethod) =>
                paymentMethod.label === 'Crédito' && paymentMethod.selected
            ),
          },
        });

        const paymentsMethodsSelected = paymentMethodsOptions.filter(
          (paymentMethod) => paymentMethod.selected
        );

        let newPaymentsMethodsData = JSON.parse(
          JSON.stringify(selectedPaymentsMethods)
        ) as IEventPaymentMethod[];

        const deletedPaymentsMethods = oldPaymentsMethods.filter(
          (oldPaymentMethod) =>
            !paymentsMethodsSelected.some(
              (paymentMethodSelected) =>
                paymentMethodSelected.value ===
                oldPaymentMethod.payment_method_id
            )
        );

        const newPaymentsMethods = paymentsMethodsSelected.filter(
          (paymentMethodSelected) =>
            !oldPaymentsMethods.some(
              (oldPaymentMethod) =>
                paymentMethodSelected.value ===
                oldPaymentMethod.payment_method_id
            )
        );

        const updatedPaymentsMethods = paymentsMethodsSelected.filter(
          (paymentMethodSelected) =>
            oldPaymentsMethods.some(
              (oldPaymentMethod) =>
                paymentMethodSelected.value ===
                oldPaymentMethod.payment_method_id
            )
        );

        if (deletedPaymentsMethods.length > 0) {
          for (const deletedPaymentMethod of deletedPaymentsMethods) {
            await api.delete(
              `events-payments-methods/${deletedPaymentMethod.id}`
            );
            newPaymentsMethodsData = newPaymentsMethodsData.filter(
              (paymentMethod) =>
                paymentMethod.payment_method_id !==
                deletedPaymentMethod.payment_method_id
            );
          }
        }

        if (newPaymentsMethods.length > 0) {
          for (const paymentMethod of newPaymentsMethods) {
            const eventPaymentMethodFormData = {
              event_id: basicData.event_id,
              payment_method_id: paymentMethod.value,
              installments:
                paymentMethod.label === 'Crédito' ? maxInstallments : undefined,
            };

            const response = await api.post(
              'events-payments-methods',
              eventPaymentMethodFormData
            );

            newPaymentsMethodsData.push({
              id: response.data.id,
              payment_method_id: paymentMethod.value as number,
              installments:
                paymentMethod.label === 'Crédito' ? maxInstallments : undefined,
            });
          }
        }

        if (updatedPaymentsMethods.length > 0) {
          for (const paymentMethod of updatedPaymentsMethods) {
            const eventPaymentMethodFormData = {
              event_id: basicData.event_id,
              payment_method_id: paymentMethod.value,
              installments:
                paymentMethod.label === 'Crédito' ? maxInstallments : undefined,
            };

            const eventPaymentMethod = oldPaymentsMethods.find(
              (oldPaymentMethod) =>
                oldPaymentMethod.payment_method_id === paymentMethod.value
            );

            await api.put(
              `events-payments-methods/${eventPaymentMethod?.id}`,
              eventPaymentMethodFormData
            );

            const index = newPaymentsMethodsData.findIndex(
              (paymentMethodData) =>
                paymentMethodData.payment_method_id === paymentMethod.value
            );

            if (index >= 0) {
              newPaymentsMethodsData[index].payment_method_id =
                paymentMethod.value as number;
              newPaymentsMethodsData[index].installments =
                paymentMethod.label === 'Crédito' ? maxInstallments : undefined;
            }
          }
        }

        await api.put(`events/organizer/${basicData.event_id}`, {
          contract_id: contractSelected.id,
          target_registration: targets.registration,
          target_revenue: targets.revenue,
          target_avarage_ticket: targets.avarageTicket,
          fill_before: beforePayment,
          unique_ticket: uniqueTicket,
          free: freePayment,
        });

        const newBatches = batches.slice();
        if (freePayment) {
          const indexBatch = 0;
          for (const batch of newBatches) {
            if (batch.batchesCategories) {
              const indexBatchCategory = 0;
              for (const batchCategory of batch.batchesCategories) {
                const batchCategoryFormData = {
                  batch_id: batchCategory.batch_id,
                  category_id: batchCategory.category_id,
                  modality_id: batchCategory.modality_id,
                  event_id: batchCategory.event_id,
                  quantity: batchCategory.quantity,
                  price: 0,
                  on: batchCategory.on,
                };

                await api.put(
                  `batches-categories/${batchCategory.id}`,
                  batchCategoryFormData
                );

                const { batchesCategories } = newBatches[indexBatch];
                if (batchesCategories) {
                  batchesCategories[indexBatchCategory].price = '0';
                  newBatches[indexBatch].batchesCategories = batchesCategories;
                }
              }
            }
          }
        }

        setSelectedPaymentsMethods(newPaymentsMethodsData);
        setOldPaymentsMethods(
          JSON.parse(JSON.stringify(newPaymentsMethodsData))
        );
        onChange(
          newPaymentsMethodsData,
          {
            ...basicData,
            contract_id: contractSelected.id,
            target_registration: targets.registration,
            target_revenue: targets.revenue,
            target_avarage_ticket: targets.avarageTicket,
            fill_before: beforePayment,
            unique_ticket: uniqueTicket,
            free: freePayment,
          },
          newBatches,
          {
            registration: targets.registration,
            revenue: targets.revenue,
            avarageTicket: targets.avarageTicket,
          }
        );
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          formRef.current?.setErrors(errors);
        } else {
          Swal.fire('Oops...', 'Credenciais inválidas!');
        }
      } finally {
        setLoading(false);
        setLoadingText('Carregando...');
      }
    },
    [
      basicData,
      batches,
      beforePayment,
      contractSelected.id,
      freePayment,
      loading,
      maxInstallments,
      oldPaymentsMethods,
      onChange,
      paymentMethodsOptions,
      selectedPaymentsMethods,
      targets.avarageTicket,
      targets.registration,
      targets.revenue,
      uniqueTicket,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        formRef.current?.submitForm();
      },
    }),
    []
  );

  return (
    <Container ref={formRef} onSubmit={handleSubmit} className="row">
      <div className="col-12">
        <h2 className="h4 fw-semibold text-center mt-5 mb-4">9 - Financeiro</h2>
      </div>
      <div className="col-lg-7 px-lg-2">
        <h3 className="d-flex h6 fw-normal position-relative">
          Inscrições serem obrigatoriamente preenchidas antes do pagamento
          <span
            className="tooltip-total"
            data-tooltip="Ativando este módulo será obrigatório o usuário identificar a quem a(s) inscrição(ões) serão destinado antes de realizar o pagamento, tal como o tamanho do kit, por exemplo. Caso não ativado, a identificação da inscrição será posterior a compra, permitindo um fluxo de compra mais curto!"
          >
            <KrInfo size={12} color="#202020" className="ms-2 mb-1" />
          </span>
        </h3>
        <InputCheckbox
          type="radio"
          name="tickets_before_payment"
          className="check-payments mt-2 justify-content-start"
          options={[
            {
              label: 'Sim',
              value: 'sim',
              selected: beforePayment === 1,
            },
            {
              label: 'Não',
              value: 'nao',
              selected: beforePayment === 0,
            },
            {
              label: 'Não necessita preenchimento',
              value: 'nao_necessita',
              selected: beforePayment === 2,
            },
          ]}
          onChange={(options) => {
            const selected = options.find((option) => option.selected);
            if (selected) {
              if (selected.value === 'sim') {
                setBeforePayment(1);
              } else if (selected.value === 'nao') {
                setBeforePayment(0);
              } else {
                setBeforePayment(2);
              }
            }
          }}
        />
      </div>
      <div className="col-lg-5 px-lg-2">
        <h3 className="d-flex h6 fw-normal position-relative">
          Evento com inscrição única por atleta?
          <span
            className="tooltip-total"
            data-tooltip="Ativando este módulo será permitido apenas uma inscrição por atleta"
          >
            <KrInfo size={12} color="#202020" className="ms-2 mb-1" />
          </span>
        </h3>
        <InputCheckbox
          type="radio"
          name="unique_ticket_per_athlete"
          className="check-payments mt-2 justify-content-start"
          options={[
            {
              label: 'Sim',
              value: 'sim',
              selected: uniqueTicket,
            },
            {
              label: 'Não',
              value: 'nao',
              selected: !uniqueTicket,
            },
          ]}
          onChange={(options) => {
            const selected = options.find((option) => option.selected);
            if (selected) {
              if (selected.value === 'sim') {
                setUniqueTicket(true);
              } else {
                setUniqueTicket(false);
              }
            }
          }}
        />
      </div>
      <div className="col-lg-4 mt-3 px-lg-2">
        <h3 className="h6 fw-normal">Contrato</h3>
        <Select
          name="contract"
          className="input"
          options={contractsOptions}
          onChange={handleChangeContract}
          placeholder="Selecione"
          disabled={sold}
        />
      </div>
      <div className="col-lg-4 mt-3 px-lg-2">
        <h3 className="h6 fw-normal">Taxa de serviço</h3>
        <Input
          readOnly
          value={`${
            contractSelected.service_type === 'Valor' ? 'R$' : ''
          }${parseFloat(contractSelected?.service || '0')
            .toFixed(2)
            .replace('.', ',')}${
            contractSelected.service_type === 'Porcentagem' ? '%' : ''
          }`}
          name="serviceTax"
          className="input"
        />
      </div>
      <div className="col-lg-4 mt-3 px-lg-2">
        <h3 className="h6 fw-normal">Taxa de processamento</h3>
        <Input
          readOnly
          value={`${
            contractSelected.tax_type === 'Valor' ? 'R$' : ''
          }${parseFloat(contractSelected?.tax || '0')
            .toFixed(2)
            .replace('.', ',')}${
            contractSelected.tax_type === 'Porcentagem' ? '%' : ''
          }`}
          name="processingTax"
          className="input"
        />
      </div>
      <div className="col-lg-4 mt-3 px-lg-2">
        <h3 className="h6 fw-normal">Meta de inscrições</h3>
        <Input
          type="number"
          min={0}
          name="targetRegistration"
          className="input"
          value={targets.registration}
          onChange={handleTargetRegistration}
          onWheel={(e: any) => e.target.blur()}
        />
      </div>
      <div className="col-lg-4 mt-3 px-lg-2">
        <h3 className="h6 fw-normal">Meta de receita</h3>
        <InputMask
          kind="money"
          value={freePayment ? formatPrice(0) : targets.revenue.toFixed(2)}
          name="targetRevenue"
          className="input"
          onChange={handleTargetRevenue}
        />
      </div>

      <div className="col-lg-4 mt-3 px-lg-2">
        <h3 className="h6 fw-normal">Meta de Ticket médio</h3>
        <Input
          readOnly
          value={
            freePayment || targets.avarageTicket === Infinity
              ? formatPrice(0)
              : formatPrice(targets.avarageTicket)
          }
          name="targetAvarageTicket"
          className="input"
        />
      </div>
      <div className="col-12 mt-4 px-lg-2">
        <h3 className="h6 fw-normal">Formas de pagamentos</h3>
        <InputCheckbox
          type="checkbox"
          name="paymentsMethods"
          className="check-payments mt-2 flex-column justify-content-start"
          options={paymentMethodsOptions}
          onChange={handleChangePaymentsMethods}
        />
        {creditPayment && (
          <>
            <h3 className="h6 fw-normal mt-3">Máximo de parcelas</h3>
            <Select
              name="maxInstallments"
              className="input"
              options={installments}
              placeholder="Selecione"
              onChange={handleChangeMaxInstallments}
            />
          </>
        )}
      </div>
      <Loading active={loading} text={loadingText} />
    </Container>
  );
};

export default forwardRef(Financial);
